import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["Target", "Outreach", "Connected", "Active", "Nurture"];
const TIERS = [1, 2, 3];
const ACTIONS = ["Send intro email","Call office","Leave voicemail","Send follow-up email","Send market overview","Schedule intro call","Book in-person meeting","Review submission","Misc. discussion","Move to nurture","Stay in touch"];

export default function AddBrokerForm({ onSuccess, onCancel }) {
    const [form, setForm] = useState({
        agency_name: "",
        producer_name: "",
        email: "",
        phone: "",
        location: "",
        tier: 2,
        status: "Target",
        estimated_book_size: 0,
        estimated_annual_premium: 0,
        current_markets: "",
        next_action: "Send intro email",
        next_followup_due: "",
        notes: "",
        owner: "",
    });
    const [saving, setSaving] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.agency_name || !form.producer_name || !form.email) {
            toast.error("Agency, producer, and email are required");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/brokers", {
                ...form,
                tier: parseInt(form.tier, 10),
                estimated_book_size: parseFloat(form.estimated_book_size) || 0,
                estimated_annual_premium: parseFloat(form.estimated_annual_premium) || 0,
            });
            toast.success(`${form.agency_name} added to pipeline`);
            onSuccess?.(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add broker");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="add-broker-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Agency name *">
                    <Input required value={form.agency_name} onChange={(e) => update("agency_name", e.target.value)} data-testid="add-agency-input" />
                </Field>
                <Field label="Producer name *">
                    <Input required value={form.producer_name} onChange={(e) => update("producer_name", e.target.value)} data-testid="add-producer-input" />
                </Field>
                <Field label="Email *">
                    <Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="add-email-input" />
                </Field>
                <Field label="Phone">
                    <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="add-phone-input" />
                </Field>
                <Field label="Location">
                    <Input value={form.location} onChange={(e) => update("location", e.target.value)} data-testid="add-location-input" />
                </Field>
                <Field label="Current markets">
                    <Input value={form.current_markets} onChange={(e) => update("current_markets", e.target.value)} placeholder="e.g. PHLY, NSM, K&K" data-testid="add-markets-input" />
                </Field>
                <Field label="Owner">
                    <Input
                        value={form.owner}
                        onChange={(e) => update("owner", e.target.value)}
                        placeholder="Who on the BD team owns this? e.g. Matt, Jay"
                        data-testid="add-owner-input"
                    />
                </Field>
                <Field label="Tier *">
                    <Select value={String(form.tier)} onValueChange={(v) => update("tier", parseInt(v, 10))}>
                        <SelectTrigger data-testid="add-tier-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>{TIERS.map(t => <SelectItem key={t} value={String(t)}>Tier {t}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Status *">
                    <Select value={form.status} onValueChange={(v) => update("status", v)}>
                        <SelectTrigger data-testid="add-status-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Est. book size ($)">
                    <Input type="number" value={form.estimated_book_size} onChange={(e) => update("estimated_book_size", e.target.value)} data-testid="add-book-input" />
                </Field>
                <Field label="Est. annual premium ($)">
                    <Input type="number" value={form.estimated_annual_premium} onChange={(e) => update("estimated_annual_premium", e.target.value)} data-testid="add-premium-input" />
                </Field>
                <Field label="Next action">
                    <Select value={form.next_action} onValueChange={(v) => update("next_action", v)}>
                        <SelectTrigger data-testid="add-action-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>{ACTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Next follow-up due">
                    <Input type="date" value={form.next_followup_due} onChange={(e) => update("next_followup_due", e.target.value)} data-testid="add-followup-input" />
                </Field>
                <Field label="Notes" full>
                    <Textarea rows={4} value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="add-notes-input" />
                </Field>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-ph-borderSoft">
                {onCancel && (
                    <Button type="button" variant="outline" onClick={onCancel} className="font-medium" data-testid="cancel-add-broker">Cancel</Button>
                )}
                <Button type="submit" disabled={saving} className="bg-ph-primary text-white font-medium hover:bg-ph-primaryHover" data-testid="submit-add-broker">
                    {saving ? "Adding…" : "Add broker"}
                </Button>
            </div>
        </form>
    );
}

function Field({ label, children, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="text-xs font-medium text-ph-text mb-1.5 block">{label}</label>
            {children}
        </div>
    );
}
